/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import ActionModel, { ActionType } from 'cio/models/action';
import { computed } from '@ember/object';
import actionInfo from 'cio/utils/action-info';
import { ALL_CHANNELS } from 'cio/utils/metrics/common';
import CampaignModel from 'cio/models/campaign';
import AuthService from 'cio/services/auth';

interface CampaignWorkflowSummaryArgs {
  campaign?: CampaignModel;
  actions?: ActionModel[];
  showAll?: boolean;
}

let explicitlyOrderedTypes = ALL_CHANNELS.map((c) => `${c}_action`) as ActionType[];

const GROUPS = [
  ...explicitlyOrderedTypes.map((t) => ({
    icon: actionInfo(t).icon,
    type: t,
  })),
  {
    icon: 'person',
    type: 'data',
  },
  {
    icon: 'time-delay',
    type: 'delay',
  },
];

export default class CampaignWorkflowSummary extends Component<CampaignWorkflowSummaryArgs> {
  @service auth!: AuthService;

  @computed('args.{campaign.workflowActions.[],actions.[]}')
  get actions() {
    if (this.args.campaign != null) {
      this.args.campaign.ensureParsedTree();
      return this.args.campaign.consolidatedActions;
    }

    if (this.args.actions != null) {
      return this.args.actions;
    }

    return [];
  }

  get showAll() {
    return this.args.showAll ?? false;
  }

  get actionsByType() {
    return this.actions.reduce<Record<string, ActionModel[]>>((acc, a) => {
      let type = '';

      if (a.isExit) {
        // don't show exits
        return acc;
      } else if (a.isDelay) {
        // Check for delays before branches, because a wait until action is also
        // a branch, and we do want to show them in the summary
        type = 'delay';
      } else if (a.isBranch) {
        // don't show branches
        return acc;
      } else if (a.isSplitTest) {
        // show split tests as emails
        type = 'email_action';
      } else if (a.isMessagingType) {
        type = a.type;
      } else if (a.info.group === 'data') {
        type = 'data';
      } else if (a.isCreateEventAction) {
        type = 'create_event_action';
      } else {
        return acc;
      }

      if (acc[type] == null) {
        acc[type] = [];
      }

      (acc[type] as ActionModel[]).push(a);
      return acc;
    }, {});
  }

  get groupedActions() {
    return GROUPS.reduce<{ icon: string; type: string; actions: ActionModel[] }[]>((acc, g) => {
      let actionsInGroup = this.actionsByType[g.type] ?? [];

      if (actionsInGroup.length === 0) {
        return acc;
      }

      return [
        ...acc,
        {
          ...g,
          actions: actionsInGroup,
        },
      ];
    }, []);
  }

  get visibleActions() {
    return this.actions.filter((a) => a.isMessagingType || a.isSplitTest || a.isDelay || a.isEffect);
  }

  get hasReadOnlyPermission() {
    if (this.args.campaign != null) {
      if (this.args.campaign.isApiTriggered) {
        return !this.auth.hasPermission('journeys/broadcasts/edit');
      }

      return !this.auth.hasPermission('journeys/campaigns/edit');
    }

    return true;
  }
}
